<template>
  <div class="bg-black/[.12] p-5 md:w-[280px] flex-initial">
    <div class="text-base leading-6 font-semibold mb-4">Order summary</div>
    <div class="space-y-4">
      <div class="flex justify-between text-xs">
        <div>
          {{ totalBillableUsers }} members x ${{ costPerMember.toFixed(2) }}
          x 1 Year
        </div>
        <div>${{ subtotalCost.toFixed(2) }}</div>
      </div>
      <div>
        <div v-if="selectedCoupon">
          <div
            class="bg-green-200/70 px-2 py-1 text-primary-900 mb-1 rounded-sm"
          >
            Applied coupon:
            <strong>{{ selectedCoupon.name }}</strong>
          </div>
          <Button
            type="button"
            variant="link"
            class="text-xs px-0 py-0 h-auto font-normal"
            @click="removeCoupon"
          >
            Remove coupon
          </Button>

          <div class="flex justify-between text-sm font-semibold mt-2">
            <div>Coupon discount</div>
            <div>${{ discount.toFixed(2) }}</div>
          </div>
        </div>
        <Collapsible v-else v-model:open="isCouponOpen">
          <CollapsibleTrigger as-child>
            <Button
              type="button"
              variant="link"
              class="text-xs px-0 py-0 h-auto font-normal"
            >
              <span v-if="!isCouponOpen">Add coupon</span>
              <span v-else>Hide coupon</span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <form id="coupon-form" @submit="onSubmit">
              <fieldset class="flex flex-col gap-1 mb-1">
                <Label for="coupon"> Coupon code </Label>
                <Input v-bind="couponAttrs" id="coupon" v-model="coupon" />
                <span class="text-error-400">{{ errors?.coupon }}</span>
              </fieldset>
              <Button
                form="coupon-form"
                size="sm"
                :loading="isPendingPromoMutation"
                :disabled="isPendingPromoMutation"
              >
                Apply coupon
              </Button>
            </form>
          </CollapsibleContent>
        </Collapsible>
      </div>
      <hr class="border-neutral-300" />
      <div class="flex justify-between text-sm font-semibold">
        <div>Due today</div>
        <div>${{ totalCost.toFixed(2) }}</div>
      </div>
      <Button
        form="pay-form"
        class="w-full"
        :loading="isPending"
        :disabled="isPending || isPendingPromoMutation"
      >
        Upgrade workspace
      </Button>
      <p class="text-neutral-500 text-xs text-center text-pretty">
        Any additional editors you add later will be prorated.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { toast } from 'vue-sonner';
import { useMutation } from '@tanstack/vue-query';

import requests from '@vuex/requests.js';

import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { Button } from '@ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@ui/components/collapsible';

const props = defineProps({
  selectedPackage: {
    type: Object,
    required: true,
  },
  isPending: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['onApplyCoupon']);

const selectedCoupon = ref(null);
const isCouponOpen = ref(false);

const validationSchema = toTypedSchema(
  z.object({
    coupon: z.string().max(255).min(1, {
      message: 'The coupon field is required',
    }),
  })
);

const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema,
  initialValues: {
    coupon: '',
  },
});
const [coupon, couponAttrs] = defineField('coupon');

const store = useStore();

const totalBillableUsers = computed(
  () => store.getters['workspace/getTotalBillableUsers']
);

const costPerMember = computed(() => {
  return (
    (props.selectedPackage.billing_cycle === 'yearly'
      ? props.selectedPackage.yearly_unit_amount
      : props.selectedPackage.unit_amount) / 100
  );
});

const subtotalCost = computed(() => {
  return totalBillableUsers.value * costPerMember.value;
});

const discount = computed(() => {
  if (selectedCoupon.value) {
    if (selectedCoupon.value.isPercentage) {
      return subtotalCost.value * (selectedCoupon.value.discount / 100);
    }
    return selectedCoupon.value.discount;
  }
  return 0;
});

// TODO
const totalCost = computed(() => {
  return Math.max(
    totalBillableUsers.value * costPerMember.value - discount.value,
    0
  );
});

// Promo code validation
const { isPending: isPendingPromoMutation, mutate: mutatePromoCodeValidation } =
  useMutation({
    mutationFn: async payload => {
      const res = await requests.workspaces.subscription.validateCoupon(
        store.getters['workspace/getId'],
        {
          ...payload,
        }
      );
      return res;
    },
    onSuccess: data => {
      if (data.valid_coupon) {
        toast.success(data.message);
        selectedCoupon.value = {
          promoCode: data.promo_code,
          name: data.coupon_name,
          discount: data.discount,
          isPercentage: data.is_percentage,
        };
        isCouponOpen.value = false;
        emits('onApplyCoupon', selectedCoupon.value);
        resetForm();
      } else {
        toast.error(data.message);
      }
    },
    onError: async () => {
      toast.error('Something went wrong, please try again later');
    },
  });
const onSubmit = handleSubmit(async values => {
  await mutatePromoCodeValidation({ ...values });
});

// Remove coupon
const removeCoupon = () => {
  selectedCoupon.value = null;
  emits('onApplyCoupon', {
    promoCode: '',
  });
};
</script>
